// RequestsDashboard1.jsx
// Default-exported component (like KnowledgeGaps.jsx).
// You can import it with any name, e.g.:
//   import Requests from './components/RequestsDashboard1';
// and render: <Requests selectedDateRange={rangeLabel} />

import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useRequestsSocket } from '../hooks/useRequestsSocket';
import { createPortal } from 'react-dom';
import { useAuth } from '../state/auth.jsx';
import DateRangeFilter from "../components/DateRangeFilter";
import { useOutletContext } from 'react-router-dom';


/* -------------------- Demo Data (replace with real data) -------------------- */
const initialRequestsData = [
  {
    id: 1, date: '15/01/24', time: '12:45', room: '203', type: 'Cleaning',
    summary: 'Housekeeping schedule',
    message: 'When will housekeeping come to clean the room today?',
    status: 'Done', comment: 'Housekeeping scheduled for 3 PM.',
    chatHistory: [
      { sender: 'Guest', text: 'When will housekeeping come to clean the room today?', time: '12:45' },
      { sender: 'Kypser AI', text: "Thank you. I’m checking right now and will update you shortly.", time: '12:45' },
    ]
  },
  {
    id: 2, date: '16/01/24', time: '14:32', room: '205', type: 'Cleaning',
    summary: 'Extra towels needed',
    message: 'Hi, I need some extra towels in room 205. We have guests arriving tonight.',
    status: 'Pending', comment: null,
    chatHistory: [
      { sender: 'Guest', text: 'Hi, I need some extra towels in room 205. We have guests arriving tonight.', time: '14:32' },
      { sender: 'Kypser AI', text: 'Of course. Extra towels are on the way.', time: '14:33' },
    ]
  },
  {
    id: 3, date: '16/01/24', time: '14:15', room: '312', type: 'Taxi',
    summary: 'Airport transport',
    message: 'Can you arrange a taxi to the airport for 6 AM tomorrow? Flight is at 8:30 AM.',
    status: 'Done', comment: 'Taxi booked with Blue Cab Co.',
    chatHistory: [
      { sender: 'Guest', text: "Can you arrange a taxi to the airport for 6 AM tomorrow? Flight is at 8:30 AM.", time: '14:15' },
      { sender: 'Kypser AI', text: 'Taxi booked for 6 AM tomorrow.', time: '14:16' },
    ]
  },
  {
    id: 4, date: '16/01/24', time: '13:58', room: '108', type: 'Room Service',
    summary: 'Late dinner order',
    message: 'Is it possible to get room service after 11 PM? We will be arriving late.',
    status: 'Pending', comment: null,
    chatHistory: [
      { sender: 'Guest', text: 'Is it possible to get room service after 11 PM? We will be arriving late.', time: '13:58' },
      { sender: 'Kypser AI', text: 'Yes, room service is available.', time: '13:59' },
    ]
  },
  {
    id: 5, date: '15/01/24', time: '13:45', room: '421', type: 'Maintenance',
    summary: 'AC not working',
    message: "The air conditioning in our room is not working properly. It's quite warm.",
    status: 'Cancelled by Reception', comment: 'Maintenance team was busy.',
    chatHistory: [
      { sender: 'Guest', text: "The air conditioning in our room is not working properly. It's quite warm.", time: '13:45' },
      { sender: 'Kypser AI', text: 'We apologize for the inconvenience. Maintenance will check it shortly.', time: '13:46' },
    ]
  },
];

const initialArchivedRequestsData = [
  {
    id: 8, date: '14/01/24', time: '12:38', room: '315', type: 'Taxi',
    summary: 'City tour transport',
    message: 'Need transportation for city tour tomorrow morning.',
    status: 'Cancelled', comment: 'Guest cancelled the tour.',
    chatHistory: [
      { sender: 'Guest', text: 'Need transportation for city tour tomorrow morning.', time: '12:38' },
      { sender: 'Kypser AI', text: 'Noted.', time: '12:40' },
    ]
  },
  {
    id: 9, date: '13/01/24', time: '11:20', room: '127', type: 'Room Service',
    summary: 'Breakfast order',
    message: "Can I get breakfast delivered to my room at 8 AM tomorrow?",
    status: 'Done', comment: 'Breakfast delivered on time.',
    chatHistory: [
      { sender: 'Guest', text: "Can I get breakfast delivered to my room at 8 AM tomorrow?", time: '11:20' },
      { sender: 'Kypser AI', text: 'Order confirmed.', time: '11:21' },
    ]
  },
  {
    id: 10, date: '12/01/24', time: '10:15', room: '289', type: 'Concierge',
    summary: 'Theater tickets',
    message: "Help me book tickets for tonight's show at the Opera House.",
    status: 'Done', comment: 'Tickets booked for 7 PM show.',
    chatHistory: [
      { sender: 'Guest', text: "Help me book tickets for tonight's show at the Opera House.", time: '10:15' },
      { sender: 'Kypser AI', text: 'Booked.', time: '10:18' },
    ]
  },
];



/* -------------------- Utilities & Small Components -------------------- */
const useOutsideClick = (ref, callback) => {
  useEffect(() => {
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) callback();
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [ref, callback]);
};

const GlobalStyles = () => (
  <style>{`
    ::-webkit-scrollbar { width: 1px; height: 1px; }
    ::-webkit-scrollbar-track { background: #f1f5f9; }
    ::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
    ::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
  `}</style>
);

const ExpandIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"
    className="lucide lucide-expand w-4 h-4 text-pink-400 hover:text-pink-600" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m21 21-6-6m6 6v-5m0 5h-5" /><path d="M3 3l6 6m-6-6v5m0-5h5" />
    <path d="M21 3l-6 6m6-6v5m0-5h-5" /><path d="M3 21l6-6m-6 6v-5m0 5h5" />
  </svg>
);
const CloseIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="lucide lucide-x h-6 w-6 text-gray-500 hover:text-gray-800" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M18 6 6 18" /><path d="m6 6 12 12" />
  </svg>
);
const ChatBubbleIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="lucide lucide-message-circle w-5 h-5 text-pink-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M2.992 16.342a2 2 0 0 1 .094 1.167l-1.065 3.29a1 1 0 0 0 1.236 1.168l3.413-.998a2 2 0 0 1 1.099.092 10 10 0 1 0-4.777-4.719"></path>
  </svg>
);
const SparklesIcon = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={`lucide lucide-sparkles ${className}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z" />
    <path d="M5 3v4" /><path d="M19 17v4" /><path d="M3 5h4" /><path d="M17 19h4" />
  </svg>
);
const SearchIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="lucide lucide-search absolute left-3 top-1/2 -translate-y-1/2 text-pink-400 w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="m21 21-4.34-4.34"></path><circle cx="11" cy="11" r="8"></circle>
  </svg>
);
const ArchiveIcon = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={`lucide lucide-archive ${className}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect width="20" height="5" x="2" y="3" rx="1"></rect><path d="M4 8v11a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8"></path><path d="M10 12h4"></path>
  </svg>
);
const UnarchiveIcon = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={`lucide lucide-archive-restore ${className}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect width="20" height="5" x="2" y="3" rx="1" />
    <path d="M4 8v11a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8" />
    <path d="m9 12 3-3 3 3" /><path d="M12 9v9" />
  </svg>
);
const PenIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="lucide lucide-pen-line w-4 h-4 mr-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4Z" />
  </svg>
);
const CheckIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="lucide lucide-check text-pink-500 w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
    <path d="M20 6 9 17l-5-5" />
  </svg>
);
const ChevronDownIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="lucide lucide-chevron-down text-gray-400 w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="m6 9 6 6 6-6" />
  </svg>
);
const FilterIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="lucide lucide-filter w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
  </svg>
);

const statusOptions = [
  { value: 'Pending', label: 'Pending', color: 'bg-yellow-400' },
  { value: 'Done', label: 'Done', color: 'bg-green-500' },
  { value: 'Cancelled by Reception', label: 'Cancelled by Reception', color: 'bg-pink-500' },
  { value: 'Cancelled', label: 'Cancelled', color: 'bg-red-500' },
];

const StatusCellDropdown = ({ request, onStatusChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const dropdownMenuRef = useRef(null);

  useOutsideClick(dropdownMenuRef, () => setIsOpen(false));

  // useOutsideClick(dropdownRef, () => setIsOpen(false));

  const currentStatusOption = statusOptions.find(opt => opt.value === request.status);
  const handleSelect = (newStatus) => { onStatusChange(request.id, newStatus); setIsOpen(false); };

  const getDropdownPosition = () => {
    if (!dropdownRef.current) return {};
    const rect = dropdownRef.current.getBoundingClientRect();
    return {
      position: 'absolute',
      top: `${rect.bottom + window.scrollY}px`,
      left: `${rect.left + window.scrollX}px`,
      width: `${rect.width + 50}px`, // Make it a bit wider than the button
    };
  };
  return (
    // The ref is now on the button to measure its position
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-center space-x-2 text-xs border border-pink-200 rounded-lg px-3 py-1.5 bg-white hover:bg-gray-50 min-w-28"
      >
        <div className="flex items-center space-x-2">
          {currentStatusOption && <div className={`w-2.5 h-2.5 rounded-full ${currentStatusOption.color}`} />}
          <span>{currentStatusOption ? currentStatusOption.label : 'Select Status'}</span>
        </div>
        <ChevronDownIcon />
      </button>

      {/* --- 3. Use createPortal to render the dropdown menu --- */}
      {isOpen && createPortal(
        <div
          ref={dropdownMenuRef}
          style={getDropdownPosition()} // Apply calculated position
          className="bg-white rounded-lg shadow-xl border border-gray-200 z-50 p-2">
          <ul>
            {statusOptions.map(option => (
              <li key={option.value}
                onClick={() => handleSelect(option.value)}
                className={`px-3 py-2 text-sm rounded-md hover:bg-gray-100 cursor-pointer flex items-center justify-between ${request.status === option.value ? 'bg-gray-100' : ''}`}>
                <div className="flex items-center space-x-3">
                  <div className={`w-3 h-3 rounded-full ${option.color}`} />
                  <span>{option.label}</span>
                </div>
                {request.status === option.value && <CheckIcon />}
              </li>
            ))}
          </ul>
        </div>,
        document.body // Render it directly into the body tag
      )}
    </div>
  );
};

const StatusFilterDropdown = ({ selectedStatus, setSelectedStatus }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  useOutsideClick(dropdownRef, () => setIsOpen(false));

  const filterStatusOptions = [{ value: 'All Statuses', label: 'All Statuses' }, ...statusOptions];
  const handleSelect = (statusValue) => { setSelectedStatus(statusValue); setIsOpen(false); };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="h-10 w-48 px-4 flex items-center justify-between border border-pink-200 bg-white rounded-full hover:bg-pink-50 transition"
      >
        <span>{selectedStatus}</span><ChevronDownIcon />
      </button>
      {isOpen && (
        <div className="absolute top-12 left-0 w-56 bg-white rounded-lg shadow-xl border border-gray-200 z-10 p-2">
          <ul>
            {filterStatusOptions.map(option => (
              <li key={option.value}
                onClick={() => handleSelect(option.value)}
                className="px-3 py-2 text-sm rounded-md hover:bg-gray-100 cursor-pointer flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  {option.color && <div className={`w-3 h-3 rounded-full ${option.color}`} />}
                  <span className={!option.color ? 'pl-6' : ''}>{option.label}</span>
                </div>
                {selectedStatus === option.value && <CheckIcon />}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

const MessageDetailsModal = ({ request, onClose, chatMessages, chatLoading, chatError }) => {
  const modalRef = useRef(null);
  useOutsideClick(modalRef, onClose);
  if (!request) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div ref={modalRef} className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6 relative">
        <div className="flex flex-col gap-2 text-left mb-4">
          <h2 className="text-lg leading-none font-semibold flex items-center space-x-2">
            <ChatBubbleIcon /><span>Chat Message Details</span>
          </h2>
          <p className="text-sm text-gray-500">View the conversation for Room {request.room}.</p>
        </div>

        <div className="grid grid-cols-2 gap-4 p-4 bg-gradient-to-r from-pink-50 to-purple-50 rounded-lg mb-4">
          <div><span className="text-sm font-medium text-gray-600">Room:</span>
            <span className="ml-2 text-xs font-bold text-purple-700 bg-purple-100 px-2 py-1 rounded-md">{request.room}</span>
          </div>
          <div><span className="text-sm font-medium text-gray-600">Type:</span>
            <span className="ml-2 text-xs font-bold text-blue-700 bg-blue-100 px-2 py-1 rounded-md">{request.type}</span>
          </div>
          <div><span className="text-sm font-medium text-gray-600">Date:</span>
            <span className="ml-2 text-sm font-mono">{request.date}</span>
          </div>
          <div><span className="text-sm font-medium text-gray-600">Time:</span>
            <span className="ml-2 text-sm font-mono">{request.time}</span>
          </div>
        </div>

        <div className="space-y-2 mb-4">
          <label className="text-sm font-medium text-gray-700">Full Message:</label>
          <div className="p-4 bg-white border border-pink-100 rounded-lg">
            <p className="text-sm leading-relaxed text-gray-800 whitespace-pre-wrap">{request.message}</p>
          </div>
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">Chat History:</label>
          <div className="max-h-64 overflow-y-auto space-y-2 p-1">
            {chatLoading && <div className="text-sm text-gray-500">Loading chat…</div>}
            {chatError && <div className="text-sm text-red-500">{chatError}</div>}
            {!chatLoading && !chatError && chatMessages.length === 0 && (
              <div className="text-sm text-gray-400">No chat yet.</div>
            )}

            {chatMessages.map((m) => {
              const isGuest = m.role === 'user';           // map role -> test UI colors
              const senderLabel = isGuest ? 'Guest' : 'Kypser AI';
              const timeText = new Date(m.created_on).toLocaleString();

              return (
                <div
                  key={m.id}
                  className={`p-3 rounded-lg ${isGuest
                    ? 'bg-bl-50 border-l-4 border-blue-400'
                    : 'bg-yeow-50 border-l-4 border-yellow-400'
                    }`}
                >
                  <div className="flex justify-between items-start mb-1">
                    <span
                      className={`text-xs font-medium ${isGuest ? 'text-blue-700' : 'text-yellow-700'
                        }`}
                    >
                      {senderLabel}
                    </span>
                    <span
                      className={`text-xs ${isGuest ? 'text-blue-600' : 'text-yellow-600'
                        }`}
                    >
                      {timeText}
                    </span>
                  </div>

                  <p
                    className={`text-sm ${isGuest ? 'text-blue-800' : 'text-yellow-800'
                      }`}
                  >
                    {m.message}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        <button onClick={onClose} className="absolute top-4 right-4 p-1 rounded-full opacity-70 hover:opacity-100 transition-opacity">
          <CloseIcon />
        </button>
      </div>
    </div>
  );
};

/* -------------------- Component -------------------- */
const RequestsDashboard1 = () => {
  const { selectedDateRange = 'last24hours', theme } = useOutletContext();
  const isLight = theme === 'light';
  // Keep your original state & UI
  const [requests, setRequests] = useState(initialRequestsData);
  const [archivedRequests, setArchivedRequests] = useState(initialArchivedRequestsData);
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [commentText, setCommentText] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('All Statuses');
  const [isGenerating, setIsGenerating] = useState(false);
  const [dailySummary, setDailySummary] = useState('');
  const [isSummaryLoading, setIsSummaryLoading] = useState(false);
  const [viewMode, setViewMode] = useState('active'); // 'active' | 'archived'
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);   // current modal thread 
  const [chatLoading, setChatLoading] = useState(false);
  const [chatError, setChatError] = useState(null);

  // Date range state
  const [from, setFrom] = useState(null); // 'YYYY-MM-DD' or null
  const [to, setTo] = useState(null);

  const rangeLabel = useMemo(
    () => (from && to ? `${from} → ${to}` : 'last24hours'),
    [from, to]
  );
  const { api } = useAuth();
  /* --------- INTEGRATION: useRequestsSocket + adapter (no CSS changes) --------- */

  // 1) Build status param from your existing viewMode
  const listParams = useMemo(
    () => ({ status: viewMode === 'archived' ? 'archived' : 'active', from, to }),
    [viewMode, from, to]
  );

  // 2) Hook: REST snapshot + WS 'request.created'/'request.updated'
  const { requests: wsList, updateRequest, addNote, archiveRequest, unarchiveRequest, socket } =
    useRequestsSocket({ listParams });

  // 3) Adapter: convert backend rows -> your UI shape {date,time,room,type,summary,message,status,comment}
  const toUI = (row) => {
    // created_on -> date/time "DD/MM/YY" / "HH:MM"
    let dd = '', tt = '';
    if (row?.created_on) {
      const d = new Date(row.created_on);
      if (!isNaN(d)) {
        const pad = (n) => String(n).padStart(2, '0');
        dd = `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${String(d.getFullYear()).slice(-2)}`;
        tt = `${pad(d.getHours())}:${pad(d.getMinutes())}`;
      }
    }
    return {
      is_archived: !!row.is_archived,
      id: row.id,
      date: row.date || dd,
      time: row.time || tt,
      room: row.room ?? row.room_number ?? row.metadata?.room_number ?? '-',
      type: row.type ?? row.request_type ?? '-',
      summary: row.summary ?? row.request_summary ?? row.metadata?.summary ?? '-',
      message: row.message ?? row.metadata?.message ?? '',
      status: row.status || 'Pending',
      comment: row.notes ?? row.comment ?? '',
      chatHistory: row.chatHistory || [],
    };
  };

  // 4) When wsList changes, drop it into the correct local array your UI already renders
  useEffect(() => {
    if (!Array.isArray(wsList)) return;
    const adapted = wsList.map(toUI);
    const filtered = adapted.filter(r => (
      viewMode === 'archived'
        ? (r.status === 'archived' || r.is_archived === true)
        : (r.status !== 'archived' && r.is_archived !== true)
    ));
    if (viewMode === 'archived') setArchivedRequests(filtered);
    else setRequests(filtered);
  }, [wsList, viewMode]);

  useEffect(() => {
    if (!selectedRequest?.id) return;
    let cancelled = false;

    (async () => {
      try {
        setChatLoading(true);
        setChatError(null);
        const { data } = await api.get(`/requests/${selectedRequest.id}/messages`);
        if (!cancelled) setChatMessages(Array.isArray(data) ? data : []);
      } catch (e) {
        if (!cancelled) setChatError('Failed to load chat history');
      } finally {
        if (!cancelled) setChatLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [selectedRequest?.id, api]);

  /* -------------------- Original handlers, now calling backend -------------------- */
  const handleCommentChange = (e) => setCommentText(e.target.value);

  const handleSaveComment = async (id) => {
    await addNote(id, commentText); // POST /api/requests/:id/notes
    setEditingCommentId(null);
  };

  const handleCancelEdit = () => setEditingCommentId(null);

  const handleStartEditing = (request) => {
    setEditingCommentId(request.id);
    setCommentText(request.comment || '');
  };

  const handleStatusChange = (id, newStatus) => {
    if (newStatus === 'archived') {
      // flip archive flag only; DO NOT touch status
      archiveRequest(id);
      return;
    }
    updateRequest(id, { status: newStatus });
  };

  const handleArchiveRequest = (id) => {
    archiveRequest(id);
  };

  const handleUnarchiveRequest = (id) => {
    unarchiveRequest(id); // status remains whatever it was
  };

  // Keep your original “AI suggestion” UX (pure UI feature)
  const handleGenerateComment = (request) => {
    setIsGenerating(true);
    setTimeout(() => {
      const suggestion = `Note: ${request.type} — ${request.summary}. Follow up with room ${request.room}.`;
      setCommentText(suggestion);
      setIsGenerating(false);
    }, 300);
  };

  const handleDailySummary = () => {
    setIsSummaryLoading(true);
    setDailySummary('');
    setTimeout(() => {
      const src = viewMode === 'archived' ? archivedRequests : requests;
      const txt = src.map(r => `• Room ${r.room}: ${r.summary} — ${r.status}`).join('\n');
      setDailySummary(txt || 'No requests today.');
      setIsSummaryLoading(false);
    }, 300);
  };

  const filteredRequests = selectedStatus === 'All Statuses' ? requests : requests.filter(req => req.status === selectedStatus);
  const filteredArchivedRequests = selectedStatus === 'All Statuses' ? archivedRequests : archivedRequests.filter(req => req.status === selectedStatus);

  const ArchivedStatusBadge = ({ status }) => {
    const cfg = {
      Done: { bg: 'bg-gradient-to-r from-green-400 to-emerald-500', text: 'Done' },
      Cancelled: { bg: 'bg-gradient-to-r from-red-500 to-rose-500', text: 'Cancelled' },
      'Cancelled by Reception': { bg: 'bg-gradient-to-r from-pink-500 to-fuchsia-500', text: 'Cancelled by Reception' },
      Pending: { bg: 'bg-gradient-to-r from-yellow-400 to-amber-500', text: 'Pending' }
    }[status] || { bg: 'bg-gray-400', text: status };
    return <span className={`px-3 py-1.5 text-xs font-bold rounded-full text-white ${cfg.bg}`}>{cfg.text}</span>;
  };

  /* -------------------- Your original JSX & classNames (unchanged) -------------------- */
  return (
    <div className="w-full bg-white rounded-xl shadow-lg p-6 min-h-[calc(100vh-8rem)] flex flex-col">
      <GlobalStyles />

      <header className="mb-6 flex items-center justify-between">
        <h2 className="text-2xl font-semibold bg-gradient-to-r from-pink-500 to-purple-600 text-transparent bg-clip-text">
          Requests
        </h2>
        <span className="text-xs text-gray-500">
          Range: <span className="font-medium">{rangeLabel}</span>
        </span>
      </header>

      <div className="flex flex-col sm:flex-row items-center gap-4 mb-6">
        <DateRangeFilter from={from} to={to} onApply={({ from: f, to: t }) => { setFrom(f); setTo(t); }} onClear={() => { setFrom(null); setTo(null); }} buttonClassName="border-pink-200" isLight={isLight} />

        <div className="relative flex-1 w-full sm:max-w-md">
          <SearchIcon />
          <input className="w-full h-10 pl-10 pr-4 border border-pink-200 rounded-full focus:ring-2 focus:ring-pink-300 focus:outline-none"
            placeholder="Search by room, type, or content..." />
        </div>
        <StatusFilterDropdown selectedStatus={selectedStatus} setSelectedStatus={setSelectedStatus} />
        {viewMode === 'active' ? (
          <>
            <button onClick={() => setViewMode('archived')}
              className="h-10 px-6 flex items-center gap-2 border border-pink-200 bg-white rounded-full text-pink-700 hover:bg-pink-50">
              <ArchiveIcon className="w-4 h-4" /><span>Archived Requests</span>
            </button>
            <button onClick={handleDailySummary} disabled={isSummaryLoading}
              className="h-10 px-6 flex items-center gap-2 bg-gradient-to-r from-pink-500 to-purple-500 text-white rounded-full hover:shadow-lg">
              <SparklesIcon className="w-4 h-4" /><span>{isSummaryLoading ? 'Generating...' : '✨ Summarize Day'}</span>
            </button>
          </>
        ) : (
          <>
            <DateRangeFilter
              from={from}
              to={to}
              onApply={({ from: f, to: t }) => { setFrom(f); setTo(t); }}
              onClear={() => { setFrom(null); setTo(null); }}
              buttonClassName="border-pink-200"
              isLight={isLight}
            />

            <button onClick={() => setViewMode('active')}
              className="h-10 px-6 flex items-center gap-2 border border-pink-200 bg-white rounded-full text-pink-700 hover:bg-pink-50">
              <ArchiveIcon className="w-4 h-4" /><span>Active Requests</span>
            </button>
          </>
        )}
      </div>

      {dailySummary && viewMode === 'active' && (
        <div className="bg-white/80 p-4 rounded-lg border border-purple-200 mb-6 shadow-sm">
          <h3 className="font-bold text-purple-700 mb-2">Daily Summary ✨</h3>
          <p className="text-sm text-gray-700 whitespace-pre-wrap">{dailySummary}</p>
        </div>
      )}

      {viewMode === 'active' ? (
        <div className="bg-white rounded-2xl border border-pink-100 shadow overflow-hidden flex-1 flex flex-col">
          <div className="flex justify-between items-center p-6 border-b border-pink-100">
            <h3 className="text-xl font-semibold bg-gradient-to-r from-pink-500 to-purple-600 text-transparent bg-clip-text">
              Active Requests
            </h3>
            <span className="text-sm font-bold text-pink-700 bg-pink-100 px-3 py-1 rounded-full">
              {requests.length} requests
            </span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-pink-50/50">
                <tr>
                  {['Date', 'Time', 'Room', 'Type', 'Summary', 'Chat Message', 'Status', 'Comment', 'Action'].map(h => (
                    <th key={h} className="p-4 font-medium text-center text-gray-600 whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredRequests.map((request, i) => (
                  <tr key={request.id}
                    className={`border-b border-pink-100 ${i % 2 === 0 ? 'bg-white' : 'bg-pink-50/30'} hover:bg-pink-100/50`}>
                    <td className="p-4 text-gray-600 font-mono whitespace-nowrap text-center">{request.date}</td>
                    <td className="p-4 text-gray-600 font-mono whitespace-nowrap text-center">{request.time}</td>
                    <td className="p-4 whitespace-nowrap text-center">
                      <span className="px-2 py-1 text-xs font-bold text-purple-700 bg-purple-100 rounded-md">{request.room}</span>
                    </td>
                    <td className="p-4 whitespace-nowrap text-center">
                      <span className="px-2 py-1 text-xs font-bold text-blue-700 bg-blue-100 rounded-md">{request.type}</span>
                    </td>
                    <td className="p-4 font-medium whitespace-nowrap text-center">{request.summary}</td>
                    <td className="p-4 text-gray-600 max-w-xs">
                      <div className="flex items-center justify-between">
                        <span className="truncate">{request.message}</span>
                        <button onClick={() => setSelectedRequest(request)} className="ml-2 p-1"><ExpandIcon /></button>
                      </div>
                    </td>
                    <td className="p-4 flex justify-center">
                      <StatusCellDropdown request={request} onStatusChange={handleStatusChange} />
                    </td>
                    <td className="p-4 w-64">
                      {editingCommentId === request.id ? (
                        <div>
                          <textarea value={commentText} onChange={handleCommentChange}
                            className="w-full p-2 border border-pink-300 rounded-md focus:ring-2 focus:ring-pink-300" rows="3" />
                          <div className="flex items-center gap-2 mt-2">
                            <button onClick={() => handleSaveComment(request.id)}
                              className="px-4 py-1.5 textxs font-semibold text-white bg-pink-500 rounded-md hover:bg-pink-600">Save</button>
                            <button onClick={handleCancelEdit}
                              className="px-4 py-1.5 text-xs font-semibold text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300">Cancel</button>
                            <button onClick={() => handleGenerateComment(request)} disabled={isGenerating}
                              className="px-3 py-1.5 text-xs font-semibold text-purple-700 bg-purple-100 rounded-md hover:bg-purple-200 flex items-center gap-1 disabled:opacity-50">
                              <SparklesIcon className="w-4 h-4" /><span>{isGenerating ? 'Generating...' : 'Suggest'}</span>
                            </button>
                          </div>
                        </div>
                      ) : (
                        request.comment ? (
                          <div onClick={() => handleStartEditing(request)} className="cursor-pointer text-gray-700">
                            {request.comment}
                          </div>
                        ) : (
                          <div onClick={() => handleStartEditing(request)} className="cursor-pointer flex items-center text-gray-400 italic hover:text-gray-600 flex justify-center items-center">
                            <PenIcon /><span>Add comment...</span>
                          </div>
                        )
                      )}
                    </td>
                    <td className="p-4 flex justify-center">
                      <button onClick={() => handleArchiveRequest(request.id)}
                        className="h-8 w-8 flex items-center justify-center text-pink-500 border border-pink-200 bg-pink-50/50 rounded-md hover:bg-pink-100 hover:border-pink-300">
                        <ArchiveIcon className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-pink-100 shadow overflow-hidden flex-1 flex flex-col">
          <div className="flex justify-between items-center p-6 border-b border-pink-100">
            <h3 className="text-xl font-semibold text-gray-700">Archived Requests</h3>
            <span className="text-sm font-bold text-pink-700 bg-pink-100 px-3 py-1 rounded-full">
              {archivedRequests.length} requests
            </span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  {['Date', 'Time', 'Room', 'Type', 'Summary', 'Chat Message', 'Status', 'Comment', 'Action'].map(h => (
                    <th key={h} className="p-4 font-medium text-left text-gray-500 whitespace-nowrap bg-white">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredArchivedRequests.map((request) => (
                  <tr key={request.id} className="border-b border-gray-100 hover:bg-gray-50/50">
                    <td className="p-4 text-gray-600 font-mono whitespace-nowrap">{request.date}</td>
                    <td className="p-4 text-gray-600 font-mono whitespace-nowrap">{request.time}</td>
                    <td className="p-4 whitespace-nowrap text-center">
                      <span className="px-2 py-1 text-xs font-bold text-purple-700 bg-purple-100 rounded-md">{request.room}</span>
                    </td>
                    <td className="p-4 whitespace-nowrap text-center">
                      <span className="px-2 py-1 text-xs font-bold text-blue-700 bg-blue-100 rounded-md">{request.type}</span>
                    </td>
                    <td className="p-4 font-medium whitespace-nowrap">{request.summary}</td>
                    <td className="p-4 text-gray-600 max-w-xs">
                      <div className="flex items-center justify-between">
                        <span className="truncate">{request.message}</span>
                        <button onClick={() => setSelectedRequest(request)} className="ml-2 p-1"><ExpandIcon /></button>
                      </div>
                    </td>
                    <td className="p-4"><ArchivedStatusBadge status={request.status} /></td>
                    <td className="p-4 text-gray-700">{request.comment}</td>
                    <td className="p-4">
                      <button onClick={() => handleUnarchiveRequest(request.id)}
                        className="h-8 px-3 flex items-center gap-2 text-green-600 border border-green-200 bg-green-50 rounded-md hover:bg-green-100 hover:border-green-300 text-xs">
                        <UnarchiveIcon className="w-4 h-4" /><span>Unarchive</span>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <MessageDetailsModal
        request={selectedRequest}
        onClose={() => { setSelectedRequest(null); setChatMessages([]); setChatError(null); }}
        chatMessages={chatMessages}
        chatLoading={chatLoading}
        chatError={chatError}
      />
    </div>
  );
};

export default RequestsDashboard1;
